import { useEffect, useId, useRef, useState } from "react";
import type { GraphNode } from "../types";
import { Icon } from "./Icon";

interface SearchBarProps {
  query: string;
  results: GraphNode[];
  typeLabels: Map<string, string>;
  onChange: (query: string) => void;
  onSelect: (nodeId: string) => void;
}

export function SearchBar({ query, results, typeLabels, onChange, onSelect }: SearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const shownResults = results.slice(0, 7);
  const hasQuery = query.trim().length > 0;

  useEffect(() => {
    setActiveIndex(-1);
  }, [query]);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping =
        target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable;

      if (event.key === "/" && !isTyping && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };

    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const commitSelection = (nodeId: string) => {
    onSelect(nodeId);
    setOpen(false);
  };

  return (
    <div className="search-region">
      <div className={`search-box${open && hasQuery ? " is-open" : ""}`}>
        <Icon className="search-icon" name="search" size={18} />
        <input
          aria-activedescendant={
            activeIndex >= 0 ? `${listId}-option-${shownResults[activeIndex]?.id}` : undefined
          }
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={open && hasQuery}
          aria-label="세컨드 브레인 검색"
          onBlur={() => window.setTimeout(() => setOpen(false), 100)}
          onChange={(event) => {
            onChange(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (!hasQuery) return;

            if (event.key === "ArrowDown") {
              event.preventDefault();
              setOpen(true);
              setActiveIndex((index) => Math.min(index + 1, shownResults.length - 1));
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setActiveIndex((index) => Math.max(index - 1, 0));
            } else if (event.key === "Enter" && activeIndex >= 0) {
              event.preventDefault();
              const activeResult = shownResults[activeIndex];
              if (activeResult) commitSelection(activeResult.id);
            } else if (event.key === "Escape") {
              setOpen(false);
              inputRef.current?.blur();
            }
          }}
          placeholder="키워드·주제로 생각의 연결 찾기"
          ref={inputRef}
          role="combobox"
          type="search"
          value={query}
        />
        {hasQuery ? (
          <button
            aria-label="검색어 지우기"
            className="search-clear"
            onClick={() => {
              onChange("");
              inputRef.current?.focus();
            }}
            type="button"
          >
            <Icon name="close" size={15} />
          </button>
        ) : (
          <kbd aria-label="슬래시 키">/</kbd>
        )}
      </div>

      {open && hasQuery ? (
        <div className="search-results" id={listId} role="listbox">
          {shownResults.length > 0 ? (
            <>
              <div className="search-result-meta" role="presentation">
                <span>검색 결과</span>
                <span>{results.length}</span>
              </div>
              {shownResults.map((node, index) => (
                <button
                  aria-selected={activeIndex === index}
                  className={activeIndex === index ? "is-active" : ""}
                  id={`${listId}-option-${node.id}`}
                  key={node.id}
                  onMouseEnter={() => setActiveIndex(index)}
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={() => commitSelection(node.id)}
                  role="option"
                  type="button"
                >
                  <span className="result-node-mark" aria-hidden="true" />
                  <span className="result-copy">
                    <strong>{node.title}</strong>
                    <span>{node.claim}</span>
                  </span>
                  <span className="result-type">{typeLabels.get(node.type) ?? node.type}</span>
                </button>
              ))}
            </>
          ) : (
            <div className="search-empty" role="status">
              <Icon name="search" size={17} />
              <div>
                <span>일치하는 공개 노드가 없습니다.</span>
                <small>키워드 일부나 주제로 다시 입력해 보세요. 예: AI, 리팩토링, 테스트</small>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
